/**
 * Runtime AWS configuration for the React app.
 *
 * Production / deployed builds: `scripts/gen-frontend-config.mjs` writes
 * `src/aws-config.generated.ts` from `cdk-outputs.json` (gitignored). This
 * module uses Vite's `import.meta.glob({ eager: true })` so the generated
 * file is picked up automatically when present and the bundler emits no
 * error when it is absent (a fresh dev checkout works out of the box with
 * the dev fallback below).
 *
 * The shape MUST match the generator output:
 *   { region, apiBase, wsUrl, userPoolId, userPoolClientId, cloudFrontUrl }
 */

export interface AwsRuntimeConfig {
  readonly region: string;
  readonly apiBase: string;
  readonly wsUrl: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  readonly cloudFrontUrl: string;
}

const DEV_FALLBACK: AwsRuntimeConfig = {
  region: "us-east-1",
  apiBase: "/api",
  wsUrl: "",
  userPoolId: "us-east-1_devUserPool",
  userPoolClientId: "devClientId",
  cloudFrontUrl: "",
};

interface GeneratedModule {
  readonly awsConfig: AwsRuntimeConfig;
}

const generatedMatches = import.meta.glob<GeneratedModule>(
  "./aws-config.generated.ts",
  { eager: true },
);

const generatedKey = Object.keys(generatedMatches)[0];
const generatedConfig =
  generatedKey !== undefined
    ? generatedMatches[generatedKey]?.awsConfig
    : undefined;

if (!generatedConfig && import.meta.env.DEV) {
  console.warn(
    "[aws-config] src/aws-config.generated.ts not found — using dev fallback. " +
      "Run scripts/gen-frontend-config.mjs after deploy to refresh.",
  );
}

/**
 * E2E overrides — when running under E2E (`VITE_E2E=true`) we ignore the
 * generated production config so tests are fully deterministic and route to
 * the local mock REST + WebSocket stubs instead of any real AWS endpoint.
 */
const env = import.meta.env as Record<string, string | undefined>;
const isE2E = env.VITE_E2E === "true";
const e2eConfig: AwsRuntimeConfig | null = isE2E
  ? {
      ...DEV_FALLBACK,
      apiBase: env.VITE_API_BASE ?? "/api",
      wsUrl: env.VITE_WS_URL ?? "ws://localhost:5173/__cy_ws",
    }
  : null;

export const awsConfig: AwsRuntimeConfig =
  e2eConfig ?? generatedConfig ?? DEV_FALLBACK;

/** True when running against a real Cognito user pool (not the dev fallback). */
export const hasRealAwsConfig = (): boolean =>
  awsConfig.userPoolId !== DEV_FALLBACK.userPoolId &&
  awsConfig.userPoolClientId !== DEV_FALLBACK.userPoolClientId;
